/**
 * StorageService v2 - Stats calculation with optional caching
 *
 * Architecture:
 * - Primary: Calculate stats from raw mentions/consequences (ALWAYS works)
 * - Performance: Cache results in stats collection (invisible optimization)
 *
 * Public API:
 * - getStats(startDate, endDate) - ONE method, always returns real data
 */

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../../firebase';

export class StorageService {
  constructor() {
    this.statsCollection = 'stats';
    this.cacheMaxAge = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get stats for any period - ALWAYS returns real data
   * This is the ONE public method consumers should use
   *
   * @param {Date} startDate - Start of period
   * @param {Date} endDate - End of period
   * @returns {Promise<Object>} Real stats, calculated from raw data
   */
  async getStats(startDate, endDate) {
    try {
      console.log('[StorageService] Getting stats for period:', {
        start: startDate.toDateString(),
        end: endDate.toDateString()
      });

      // PRIMARY PATH: Calculate from raw data (source of truth)
      const stats = await this.calculateStatsFromRawData(startDate, endDate);

      // PERFORMANCE LAYER: Cache management (non-blocking)
      this.manageCacheInBackground(startDate, endDate, stats).catch(err => {
        console.warn('[StorageService] Cache write failed (non-critical):', err);
      });

      return stats;
    } catch (error) {
      console.error('[StorageService] Failed to get stats:', error);
      throw error;
    }
  }

  /**
   * Get today's stats - convenience wrapper
   * @returns {Promise<Object>}
   */
  async getTodayStats() {
    const today = new Date();
    const start = new Date(today);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);
    return await this.getStats(start, end);
  }

  /**
   * Get stats for a period (alias for getStats)
   * @param {Date} startDate
   * @param {Date} endDate
   * @returns {Promise<Object>}
   */
  async getStatsForPeriod(startDate, endDate) {
    return await this.getStats(startDate, endDate);
  }

  /**
   * Calculate stats from raw mentions and consequences
   * This is the source of truth - ALWAYS works
   *
   * @param {Date} startDate
   * @param {Date} endDate
   * @returns {Promise<Object>}
   */
  async calculateStatsFromRawData(startDate, endDate) {
    console.log('[StorageService] Calculating from raw data...');

    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    // Fetch mentions
    const mentionsQuery = query(
      collection(db, 'mentions'),
      where('issuedAt', '>=', startTimestamp),
      where('issuedAt', '<=', endTimestamp)
    );

    // Fetch consequences (stored in disciplineQueue collection)
    const consequencesQuery = query(
      collection(db, 'disciplineQueue'),
      where('issuedAt', '>=', startTimestamp),
      where('issuedAt', '<=', endTimestamp)
    );

    const [mentionsSnapshot, consequencesSnapshot] = await Promise.all([
      getDocs(mentionsQuery),
      getDocs(consequencesQuery)
    ]);

    console.log('[StorageService] Raw snapshot sizes:', {
      mentionsDocsCount: mentionsSnapshot.docs.length,
      mentionsSize: mentionsSnapshot.size,
      consequencesDocsCount: consequencesSnapshot.docs.length,
      consequencesSize: consequencesSnapshot.size
    });

    const mentions = mentionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      issuedAt: doc.data().issuedAt?.toDate()
    }));

    const consequences = consequencesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      issuedAt: doc.data().issuedAt?.toDate()
    }));

    console.log('[StorageService] Found:', {
      mentions: mentions.length,
      consequences: consequences.length
    });

    // Aggregate the data
    return this.aggregateData(mentions, consequences);
  }

  /**
   * Aggregate mentions and consequences into stats object
   * @param {Array} mentions
   * @param {Array} consequences
   * @returns {Object}
   */
  aggregateData(mentions, consequences) {
    const stats = {
      totals: {
        mentions: mentions.length,
        consequences: consequences.length,
        uniqueStudents: new Set(mentions.map(m => m.studentId)).size,
        autoGeneratedConsequences: consequences.filter(c => c.autoGenerated).length,
        escalatedConsequences: consequences.filter(c => c.wasEscalated || c.escalatedFrom).length
      },
      bySeverity: { low: 0, medium: 0, high: 0 },
      byPeriod: {},
      hourly: this.createHourlyStructure(),
      byStudent: {},
      byTeacher: {},
      byClass: {},
      byOffense: {},
      byConsequenceType: {},
      byRule: {}
    };

    // Aggregate mentions
    mentions.forEach(mention => {
      // Severity
      const severity = mention.severity || 'low';
      stats.bySeverity[severity] = (stats.bySeverity[severity] || 0) + 1;

      // Hourly
      const hour = mention.issuedAt.getHours();
      stats.hourly[hour] = (stats.hourly[hour] || 0) + 1;

      // Period (simplified - 8am-3pm school day)
      const period = this.getPeriodFromTime(mention.issuedAt);
      if (!stats.byPeriod[period]) {
        stats.byPeriod[period] = { mentions: 0, consequences: 0 };
      }
      stats.byPeriod[period].mentions++;

      // Student
      if (!stats.byStudent[mention.studentId]) {
        stats.byStudent[mention.studentId] = {
          name: mention.studentName,
          mentions: 0,
          consequences: 0
        };
      }
      stats.byStudent[mention.studentId].mentions++;

      // Teacher
      const teacherId = mention.issuedById || 'unknown';
      if (!stats.byTeacher[teacherId]) {
        stats.byTeacher[teacherId] = {
          name: mention.issuedByName || 'Unknown',
          mentions: 0
        };
      }
      stats.byTeacher[teacherId].mentions++;

      // Class
      const classId = mention.classId || 'unassigned';
      if (!stats.byClass[classId]) {
        stats.byClass[classId] = {
          name: mention.className || 'Unassigned',
          mentions: 0
        };
      }
      stats.byClass[classId].mentions++;

      // Offense
      const offenseName = mention.offense || mention.offence || 'Unknown';
      if (!stats.byOffense[offenseName]) {
        stats.byOffense[offenseName] = {
          originalName: offenseName,
          count: 0,
          severity: mention.severity
        };
      }
      stats.byOffense[offenseName].count++;
    });

    // Aggregate consequences
    consequences.forEach(consequence => {
      // Period
      const period = this.getPeriodFromTime(consequence.issuedAt);
      if (!stats.byPeriod[period]) {
        stats.byPeriod[period] = { mentions: 0, consequences: 0 };
      }
      stats.byPeriod[period].consequences++;

      // Student
      if (!stats.byStudent[consequence.studentId]) {
        stats.byStudent[consequence.studentId] = {
          name: consequence.studentName,
          mentions: 0,
          consequences: 0
        };
      }
      stats.byStudent[consequence.studentId].consequences++;

      // Consequence type
      const type = consequence.type || 'unknown';
      if (!stats.byConsequenceType[type]) {
        stats.byConsequenceType[type] = {
          originalName: type,
          count: 0,
          autoGenerated: 0
        };
      }
      stats.byConsequenceType[type].count++;
      if (consequence.autoGenerated) {
        stats.byConsequenceType[type].autoGenerated++;
      }

      // Rule
      if (consequence.triggeredByRule) {
        const ruleId = consequence.triggeredByRule;
        if (!stats.byRule[ruleId]) {
          stats.byRule[ruleId] = {
            name: consequence.ruleName || ruleId,
            consequences: 0
          };
        }
        stats.byRule[ruleId].consequences++;
      }
    });

    return stats;
  }

  /**
   * Get period name from time
   * @param {Date} time
   * @returns {string}
   */
  getPeriodFromTime(time) {
    const hour = time.getHours();
    const minute = time.getMinutes();
    const totalMinutes = hour * 60 + minute;

    if (totalMinutes < 480) return 'before-school';
    if (totalMinutes < 540) return 'period-1';
    if (totalMinutes < 600) return 'period-2';
    if (totalMinutes < 660) return 'period-3';
    if (totalMinutes < 720) return 'lunch';
    if (totalMinutes < 780) return 'period-4';
    if (totalMinutes < 840) return 'period-5';
    if (totalMinutes < 900) return 'period-6';
    return 'after-school';
  }

  /**
   * Create empty hourly structure
   * @returns {Object}
   */
  createHourlyStructure() {
    const hourly = {};
    for (let i = 0; i < 24; i++) {
      hourly[i] = 0;
    }
    return hourly;
  }

  /**
   * Calculate typical day average from recent stats
   * @param {number} days - Number of days to look back
   * @returns {Promise<Object>}
   */
  async calculateTypicalDay(days = 28) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const stats = await this.getStats(startDate, endDate);

    // Group by day
    const dailyCounts = {};

    // We need to fetch daily to calculate typical day properly
    // For now, simplified: use total / days
    const avgPerDay = stats.totals.mentions / days;

    return {
      average: Math.round(avgPerDay),
      min: 0, // TODO: calculate properly if needed
      max: stats.totals.mentions
    };
  }

  /**
   * Get recent daily stats (for baseline calculations)
   * @param {number} days
   * @returns {Promise<Array>}
   */
  async getRecentDailyStats(days = 28) {
    const result = [];
    const today = new Date();

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);

      const start = new Date(date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(date);
      end.setHours(23, 59, 59, 999);

      const stats = await this.getStats(start, end);
      result.push(stats);
    }

    return result;
  }

  /**
   * Manage cache in background (non-blocking performance layer)
   * @param {Date} startDate
   * @param {Date} endDate
   * @param {Object} stats
   * @returns {Promise<void>}
   */
  async manageCacheInBackground(startDate, endDate, stats) {
    // Only cache single-day stats for now (simpler)
    const isSingleDay = startDate.toDateString() === endDate.toDateString();
    if (!isSingleDay) return;

    const dateKey = this.getDateKey(startDate);
    const cacheRef = doc(db, this.statsCollection, dateKey);

    // Write to cache (fire and forget)
    await setDoc(cacheRef, {
      ...stats,
      meta: {
        dateKey,
        cachedAt: Timestamp.now(),
        date: Timestamp.fromDate(startDate)
      }
    }, { merge: true });

    console.log('[StorageService] Cached stats for', dateKey);
  }

  /**
   * Get date key (YYYY-MM-DD)
   * @param {Date} date
   * @returns {string}
   */
  getDateKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Keep old methods for backwards compatibility during transition
  async recordMention(event) {
    // No-op for now - we calculate on-demand
    console.log('[StorageService] recordMention called (no-op in v2)');
  }

  async recordConsequence(event) {
    // No-op for now - we calculate on-demand
    console.log('[StorageService] recordConsequence called (no-op in v2)');
  }
}

export const storageService = new StorageService();
